import { makeStyles, tokens, Tooltip, useRestoreFocusTarget, useThemeClassName } from '@fluentui/react-components';
import { Navigation24Regular, Settings24Regular, ShieldTask24Regular } from '@fluentui/react-icons';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useAuth } from '../lib/auth/hooks';
import { canReviewUnconfirmed, isSignedInRole } from '../lib/auth/roles';
import { usePageTitle } from '../lib/context/PageTitleContext';
import { useI18n } from '../lib/i18n/hooks';
import { themes } from '../lib/theme';
import { useTheme } from '../lib/theme/ThemeContext';
import { AppNavigation } from './AppNavigation';
import { UserMenu } from './UserMenu';

const useStyles = makeStyles({
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 1001,
    padding: 0,
    backgroundColor: '#0078d4',
    borderBottom: 'none',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
  },
  navList: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  appTitle: {
    color: '#ffffff',
    textDecoration: 'none',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: '#ffffff',
      outlineStyle: 'solid',
      outlineOffset: '-2px',
    },
  },
  // Page title that replaces app title when scrolled (both mobile and desktop)
  pageTitle: {
    display: 'flex',
    color: '#ffffff',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: '100%',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    animationName: {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: '0.2s',
    animationTimingFunction: 'ease-out',
  },
  // Hide app title link on mobile (hamburger menu + mobile title used instead)
  desktopAppTitle: {
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  // Mobile-only app title (non-clickable, shows before scroll)
  mobileAppTitle: {
    display: 'none',
    color: '#ffffff',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: '100%',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  userMenuWrapper: {
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      marginLeft: 'auto',
    },
  },
  iconButton: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    padding: `0 ${tokens.spacingHorizontalM}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: 0,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    fontSize: '24px',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: '#ffffff',
      outlineStyle: 'solid',
      outlineOffset: '-2px',
    },
  },
  navItemsWrapper: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginLeft: 'auto',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  navTextButton: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    padding: `0 ${tokens.spacingHorizontalM}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: 0,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: '#ffffff',
      outlineStyle: 'solid',
      outlineOffset: '-2px',
    },
  },
  iconButtonActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: '#0078d4',
  },
  // Mobile hamburger menu styles
  mobileMenuWrapper: {
    display: 'none',
    height: '100%',
    '@media (max-width: 768px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  hamburgerButton: {
    background: 'transparent',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    padding: `0 ${tokens.spacingHorizontalM}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: 0,
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: '#ffffff',
      outlineStyle: 'solid',
      outlineOffset: '-2px',
    },
  },
});

export function TopBar() {
  const styles = useStyles();
  const themeClassName = useThemeClassName();
  const { data: session } = useAuth();
  const { t } = useI18n();
  const { resolvedTheme } = useTheme();
  const { pageTitle } = usePageTitle();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const restoreFocusTargetAttributes = useRestoreFocusTarget();

  const isAdmin = session?.user.role === 'admin';
  const isAuthenticated = isSignedInRole(session?.user.role);
  const canMerge = isSignedInRole(session?.user.role);
  const canReview = canReviewUnconfirmed(session);
  const isSettingsActive = location.pathname.startsWith('/settings');
  const isAdminActive = location.pathname.startsWith('/admin');
  const isServiceRequestsActive = location.pathname.startsWith('/service-requests');
  // Editing an unconfirmed group from the admin list links to /items/{id}/edit
  // with ?from=admin-unconfirmed. Keep the "Unconfirmed Asset Groups" tab lit
  // there instead of the generic "Items" tab.
  const fromAdminUnconfirmed = (location.search as { from?: string }).from === 'admin-unconfirmed';
  const isItemsActive = location.pathname.startsWith('/items') && !fromAdminUnconfirmed;
  const isMergeSuggestionsActive = location.pathname.startsWith('/merge-suggestions');
  const isUnconfirmedGroupsActive =
    location.pathname.startsWith('/unconfirmed-groups') ||
    (location.pathname.startsWith('/items') && fromAdminUnconfirmed);

  const activeBackgroundColor = themes[resolvedTheme].colorNeutralBackground1;

  // Show page title when scrolled (replaces app title on both mobile and desktop)
  const displayedPageTitle = pageTitle;

  return (
    <>
      {/* Mobile NavDrawer */}
      <AppNavigation isOpen={isMobileNavOpen} onOpenChange={setIsMobileNavOpen} type="overlay" />

      <nav className={`${styles.nav} ${themeClassName}`}>
        <ul className={styles.navList}>
          {/* Mobile hamburger button */}
          <div className={styles.mobileMenuWrapper}>
            <Tooltip content={t('navigation.openMenu', 'Open navigation')} relationship="label">
              <button
                className={styles.hamburgerButton}
                onClick={() => setIsMobileNavOpen(true)}
                {...restoreFocusTargetAttributes}
                aria-expanded={isMobileNavOpen}
                aria-label={t('navigation.openMenu', 'Open navigation')}
              >
                <Navigation24Regular />
              </button>
            </Tooltip>
          </div>
          {/* Desktop: App title link - hidden when page title is showing (on scroll) */}
          {!displayedPageTitle && (
            <Link to="/" className={`${styles.appTitle} ${styles.desktopAppTitle}`}>
              {t('common.appName')}
            </Link>
          )}
          {/* Mobile: App name (non-clickable) - hidden when page title is showing (on scroll) */}
          {!displayedPageTitle && <span className={styles.mobileAppTitle}>{t('common.appName')}</span>}
          {/* Page title that replaces app title on scroll (both mobile and desktop) */}
          {displayedPageTitle && (
            <span key={displayedPageTitle} className={styles.pageTitle}>
              {displayedPageTitle}
            </span>
          )}
          {/* Desktop navigation */}
          <div className={styles.navItemsWrapper}>
            {isAuthenticated && (
              <>
                <button
                  className={styles.navTextButton}
                  style={{
                    backgroundColor: isServiceRequestsActive ? activeBackgroundColor : 'transparent',
                    color: isServiceRequestsActive ? '#0078d4' : 'rgba(255, 255, 255, 0.7)',
                  }}
                  onClick={() => navigate({ to: '/service-requests' })}
                >
                  {t('navigation.service-requests')}
                </button>
                <button
                  className={styles.navTextButton}
                  style={{
                    backgroundColor: isItemsActive ? activeBackgroundColor : 'transparent',
                    color: isItemsActive ? '#0078d4' : 'rgba(255, 255, 255, 0.7)',
                  }}
                  onClick={() => navigate({ to: '/items' })}
                >
                  {t('navigation.items')}
                </button>
                {canMerge && (
                  <button
                    className={styles.navTextButton}
                    style={{
                      backgroundColor: isMergeSuggestionsActive ? activeBackgroundColor : 'transparent',
                      color: isMergeSuggestionsActive ? '#0078d4' : 'rgba(255, 255, 255, 0.7)',
                    }}
                    onClick={() => navigate({ to: '/merge-suggestions' })}
                  >
                    {t('admin.merge-suggestions.title', 'Merge suggestions')}
                  </button>
                )}
                {canReview && (
                  <button
                    className={styles.navTextButton}
                    style={{
                      backgroundColor: isUnconfirmedGroupsActive ? activeBackgroundColor : 'transparent',
                      color: isUnconfirmedGroupsActive ? '#0078d4' : 'rgba(255, 255, 255, 0.7)',
                    }}
                    onClick={() => navigate({ to: '/unconfirmed-groups' })}
                  >
                    {t('admin.unconfirmed-groups.title', 'Unconfirmed Asset Groups')}
                  </button>
                )}
              </>
            )}
            {isAuthenticated && (
              <>
                <button
                  className={styles.iconButton}
                  style={{
                    backgroundColor: isSettingsActive ? activeBackgroundColor : 'transparent',
                    color: isSettingsActive ? '#0078d4' : 'rgba(255, 255, 255, 0.7)',
                  }}
                  aria-label="Settings"
                  title="Settings"
                  onClick={() => navigate({ to: '/settings' })}
                >
                  <Settings24Regular />
                </button>
              </>
            )}
            {isAdmin && (
              <button
                className={styles.iconButton}
                style={{
                  backgroundColor: isAdminActive ? activeBackgroundColor : 'transparent',
                  color: isAdminActive ? '#0078d4' : 'rgba(255, 255, 255, 0.7)',
                }}
                aria-label="Admin"
                title="Admin"
                onClick={() => navigate({ to: '/admin/api-keys' })}
              >
                <ShieldTask24Regular />
              </button>
            )}
          </div>
          {/* Desktop user menu */}
          <div className={styles.userMenuWrapper}>
            <UserMenu />
          </div>
        </ul>
      </nav>
    </>
  );
}
